import { getSafeRedirectPath, useLoginEmailPassword } from "@qw/auth";
import { useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { Button } from "../button";
import { CardContent, CardFooter } from "../card";
import { FieldCheckbox, FieldInput, Form, FormSubmit } from "../form";
import { Group } from "../group";
import { AuthCard } from "./auth-card";

const defaultValues = {
  email: "",
  password: "",
  rememberMe: true,
};

const validationSchema = z.object({
  email: z.string().min(1, { message: "Please enter your email address" }),
  password: z.string().min(1, { message: "Please enter your password" }),
  rememberMe: z.boolean(),
});

export const AuthSignIn = () => {
  const navigate = useNavigate();
  const [loginEmailPassword, { error, loading: isLoading }] = useLoginEmailPassword();
  const [searchParams] = useSearchParams();

  /**
   * A ref, not `isLoading` and not `formState.isSubmitting`.
   *
   * Over two days on the brokerage app, 41 accepted logins were followed by a second login under
   * three seconds later with no account fetch in between; 26 of those from a per-device IPv6
   * address with an identical user agent, so it is one client submitting twice rather than two
   * people. Neither of the state-based guards closes that: react-hook-form's `handleSubmit` is not
   * re-entrant-safe and awaits async validation before `isSubmitting` matters, and a `disabled`
   * button cannot stop a native form submit that was already dispatched (Enter twice, or Enter and
   * a click) before React commits. A ref is set synchronously, inside the handler, so the second
   * call returns before it can send anything.
   */
  const submitInFlight = useRef(false);

  /**
   * Gated on the token rather than fired from `onCompleted`, and sanitized rather than passed
   * straight to `navigate`. The `?redirect=` parameter is user-controlled: `?redirect=/login`
   * self-loops back onto this form, and `?redirect=//evil.com` is a protocol-relative path that
   * `pushState` rejects cross-origin, at which point react-router's history falls back to
   * `window.location.assign` — an off-site navigation. `replace` so Back does not land here again.
   */
  const onSubmitEmailPassword = async (data: z.infer<typeof validationSchema>) => {
    if (submitInFlight.current) {
      return;
    }

    submitInFlight.current = true;

    try {
      const result = await loginEmailPassword({ variables: data });

      if (result.data?.loginEmailPassword.token) {
        navigate(getSafeRedirectPath(searchParams.get("redirect")), { replace: true });
      }
    } finally {
      // Released either way: a rejected credential has to be re-submittable, and on success this
      // component is unmounting anyway.
      submitInFlight.current = false;
    }
  };

  return (
    <Form onSubmit={onSubmitEmailPassword} defaultValues={defaultValues} validationSchema={validationSchema}>
      <AuthCard
        header="Sign in"
        subHeader="Welcome back! Please enter your credentials to access your account."
        error={error}
      >
        <CardContent>
          <Group>
            <Group className="gap-2">
              <FieldInput name="email" type="email" placeholder="Email" />
              <FieldInput name="password" type="password" placeholder="Password" />
            </Group>
            <FieldCheckbox name="rememberMe" label="Remember me" />
          </Group>
        </CardContent>
        <CardFooter>
          <Group className="flex-1">
            <FormSubmit className="grow" disabled={isLoading || false}>
              Sign In
            </FormSubmit>
            <Button variant="link" disabled={isLoading} asChild>
              <Link to="/forgot-password">Forgot your password?</Link>
            </Button>
          </Group>
        </CardFooter>
      </AuthCard>
    </Form>
  );
};
